import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "Services", href: "/services" },
  { name: "Gallery", href: "/gallery" },
  { name: "Reviews", href: "/reviews" },
  { name: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="section-padding py-4 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="font-heading text-2xl md:text-3xl tracking-tight"
          data-testid="nav-logo"
        >
          <span className={scrolled ? "text-obsidian" : "text-white"}>
            Illuminate
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={`text-sm uppercase tracking-widest transition-colors hover:text-gold ${
                scrolled ? "text-neutral-600" : "text-white/80"
              } ${location.pathname === link.href ? "text-gold" : ""}`}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+917837774865"
            className={`flex items-center gap-2 text-sm ${
              scrolled ? "text-obsidian" : "text-white"
            }`}
            data-testid="nav-phone"
          >
            <Phone size={16} />
            <span>078377 74865</span>
          </a>
          <Link
            to="/book"
            className="relative bg-gold text-white hover:bg-gold-dark rounded-full px-8 py-3 text-sm uppercase tracking-widest transition-all duration-300 shadow-lg shadow-gold/30 font-medium animate-pulse hover:animate-none"
            data-testid="nav-book-btn"
          >
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            Book Now
          </Link>
        </div>

        {/* Mobile Book + Menu */}
        <div className="lg:hidden flex items-center gap-3">
          <Link
            to="/book"
            className="relative bg-gold text-white rounded-full px-4 py-2 text-xs uppercase tracking-wider font-medium shadow-lg shadow-gold/30"
            data-testid="nav-book-btn-mobile"
          >
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            Book
          </Link>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`p-2 ${scrolled ? "text-obsidian" : "text-white"}`}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
            data-testid="mobile-menu"
          >
            <div className="section-padding py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-lg font-medium ${
                    location.pathname === link.href
                      ? "text-gold"
                      : "text-obsidian"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t flex flex-col gap-3">
                <a
                  href="tel:+917837774865"
                  className="flex items-center gap-2 text-obsidian"
                >
                  <Phone size={18} />
                  <span>078377 74865</span>
                </a>
                <Link to="/book" className="btn-gold text-center">
                  Book Appointment
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
